import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useBreakpoint } from '@ocean/hooks'
import { common, controls, modals } from '@ocean/ui'
import { useSelector } from 'hooks'
import { ActionsLayout } from 'layouts'
import { ScheduleSessions } from 'modules/simulatorTrainings/common/components'
import { namespace } from 'modules/simulatorTrainings/common/localization'
import {
  stopSimulatorTrainingSessionRequest,
  loadSimulatorTrainingSessionLinkData,
  setSimulatorTrainingVirtualClassroomSessionLink,
} from 'modules/simulatorTrainings/common/slices/instructor'
import {
  SimulatorTrainingScheduleSession,
  SimulatorTrainingSessionType,
} from 'modules/simulatorTrainings/common/types'

const { Note, Spacer, Typography } = common
const { Button, ButtonVariant } = controls
const { PlayerModal, Popup } = modals

const I18_PATH = `${namespace}:simulatorTraining.scheduleSession`

type ScheduleSessionsContainerProps = {
  sessions: SimulatorTrainingScheduleSession[]
}

const ScheduleSessionsContainer: React.FC<ScheduleSessionsContainerProps> = props => {
  const isDesktop = useBreakpoint('sm')
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { sessions } = props

  const simulatorTrainingVirtualClassroomSessionLink = useSelector(
    ({ simulatorTrainingInstructor }) =>
      simulatorTrainingInstructor.simulatorTrainingVirtualClassroomSessionLink
  )

  // Clear virtual classroom session link on unmount
  useEffect(
    () => () => {
      dispatch(setSimulatorTrainingVirtualClassroomSessionLink(null))
    },
    [dispatch]
  )

  const [isConfirmStopSimulationPopupOpen, setConfirmStopSimulationPopupOpen] = useState(false)
  const [sessionId, setSessionId] = useState('')

  function handleStart(sessionId: Guid) {
    dispatch(
      loadSimulatorTrainingSessionLinkData(sessionId, SimulatorTrainingSessionType.VirtualClassroom)
    )
  }

  function handleStartSimulation(sessionId: Guid) {
    dispatch(
      loadSimulatorTrainingSessionLinkData(
        sessionId,
        SimulatorTrainingSessionType.SimulatorEnvironment
      )
    )
  }

  function handleCloseModal() {
    dispatch(setSimulatorTrainingVirtualClassroomSessionLink(null))
  }

  function handleShowConfirmStopSimulationPopup(sessionId: Guid) {
    setSessionId(sessionId)
    setConfirmStopSimulationPopupOpen(true)
  }

  function handleCloseConfirmStopSimulationPopup() {
    setConfirmStopSimulationPopupOpen(false)
  }

  function handleStopSimulation() {
    setConfirmStopSimulationPopupOpen(false)
    dispatch(stopSimulatorTrainingSessionRequest(sessionId))
  }

  function renderConfirmStopSimulationPopupContent() {
    return (
      <>
        <Note dataTest='ConfirmStopSimulationPopupNote'>
          <Typography variant='h3' dataTest='ConfirmStopSimulationPopupNoteDescription'>
            {t(`${I18_PATH}.confirmStopSimulationMessage1`)}
            <br />
            {t(`${I18_PATH}.confirmStopSimulationMessage2`)}
          </Typography>
        </Note>
        <Spacer />
        <Spacer />
        <ActionsLayout direction='spaceBetween'>
          <Button
            title={t(`${I18_PATH}.stop`)}
            variant={ButtonVariant.Danger}
            onClick={handleStopSimulation}
            dataTest='ConfirmStopSimulationPopupStop'
          />
          <Button
            title={t(`${I18_PATH}.return`)}
            variant={ButtonVariant.Primary}
            onClick={handleCloseConfirmStopSimulationPopup}
            dataTest='ConfirmStopSimulationPopupClose'
          />
        </ActionsLayout>
      </>
    )
  }

  return (
    <>
      <ScheduleSessions
        sessions={sessions}
        onStart={handleStart}
        onStartSimulation={handleStartSimulation}
        onStopSimulation={handleShowConfirmStopSimulationPopup}
      />
      <PlayerModal
        isOpen={!!simulatorTrainingVirtualClassroomSessionLink}
        url={simulatorTrainingVirtualClassroomSessionLink || ''}
        onClose={handleCloseModal}
      />
      <Popup
        isOpen={isConfirmStopSimulationPopupOpen}
        onClose={handleCloseConfirmStopSimulationPopup}
        width={isDesktop ? '580px' : undefined}
        title={t(`${I18_PATH}.activeSimulatorSession`)}
        dataTest='ConfirmStopSimulationPopup'
      >
        {renderConfirmStopSimulationPopupContent()}
      </Popup>
    </>
  )
}

export default ScheduleSessionsContainer
